import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { H2, H3, Text, Image, MarkdownWrapper } from 'components/atoms';
import './StepContent.scss';

const StepContent = ({ title, description, steps = [], tipCard }) => {
	const [activeStep, setActiveStep] = useState(0);
	const tabRefs = useRef([]);
	const keyboardNav = useRef(false);

	useEffect(() => {
		if (keyboardNav.current) {
			tabRefs.current[activeStep]?.focus();
			keyboardNav.current = false;
		}
	}, [activeStep]);

	const handleKeyDown = (e) => {
		if (e.key === 'ArrowRight') {
			keyboardNav.current = true;
			setActiveStep((prev) => (prev + 1) % steps.length);
		} else if (e.key === 'ArrowLeft') {
			keyboardNav.current = true;
			setActiveStep((prev) => (prev - 1 + steps.length) % steps.length);
		} else if (e.key === 'Home') {
			e.preventDefault();
			keyboardNav.current = true;
			setActiveStep(0);
		} else if (e.key === 'End') {
			e.preventDefault();
			keyboardNav.current = true;
			setActiveStep(steps.length - 1);
		}
	};

	if (!steps.length) return null;

	return (
		<div className="step-content main-content">
			<div className="step-content__header">
				<H2
					className="step-content__title"
					weight="extraBold"
					color="white"
				>
					{title}
				</H2>
				<div className="step-content__description">
					<MarkdownWrapper
						content={[{ value: description }]}
						componentProps={{ color: 'white' }}
					/>
				</div>
			</div>

			{/* Desktop: horizontal tabs + separate body */}
			<div className="step-content__desktop">
				<div
					className="step-content__tabs"
					role="tablist"
					aria-label={title}
				>
					{steps.map((step, index) => (
						<button
							key={step.label}
							ref={(el) => {
								tabRefs.current[index] = el;
							}}
							id={`step-tab-${index}`}
							className={`step-content__tab${index === activeStep ? ' step-content__tab--active' : ''}`}
							role="tab"
							aria-selected={index === activeStep}
							aria-controls={`step-panel-${index}`}
							tabIndex={index === activeStep ? 0 : -1}
							onClick={() => setActiveStep(index)}
							onKeyDown={handleKeyDown}
							type="button"
						>
							<H3
								className="step-content__tab-label"
								weight={
									index === activeStep
										? 'extraBold'
										: 'standard'
								}
								color="white"
							>
								{step.label}
							</H3>
							<div className="step-content__tab-line" />
						</button>
					))}
				</div>

				{steps.map((step, index) => (
					<div
						key={step.label}
						id={`step-panel-${index}`}
						className={`step-content__body${index !== activeStep ? ' step-content__body--hidden' : ''}`}
						role="tabpanel"
						aria-labelledby={`step-tab-${index}`}
						tabIndex={0}
					>
						<MarkdownWrapper content={[{ value: step.content }]} />
					</div>
				))}
			</div>

			{/* Mobile: vertical accordion */}
			<div className="step-content__mobile">
				{steps.map((step, index) => (
					<div
						key={step.label}
						className={`step-content__accordion ${index === activeStep ? 'step-content__accordion--active' : ''}`}
					>
						<button
							className="step-content__accordion-header"
							aria-expanded={index === activeStep}
							aria-controls={`step-accordion-panel-${index}`}
							onClick={() => setActiveStep(index)}
							type="button"
						>
							<Text
								className="step-content__accordion-label"
								size="standard"
								weight={
									index === activeStep
										? 'extraBold'
										: 'standard'
								}
								color="white"
							>
								{step.label}
							</Text>
							<div className="step-content__accordion-line" />
						</button>
						<div
							id={`step-accordion-panel-${index}`}
							className={`step-content__accordion-body${index !== activeStep ? ' step-content__accordion-body--hidden' : ''}`}
						>
							<MarkdownWrapper
								content={[{ value: step.content }]}
							/>
						</div>
					</div>
				))}
			</div>

			{tipCard && (
				<div className="step-content__tip-card">
					{tipCard.icon && (
						<div className="step-content__tip-icon">
							<Image
								src={tipCard.icon}
								alt="tip icon"
								className="step-content__tip-image"
							/>
						</div>
					)}
					<div className="step-content__tip-text">
						<H3
							className="step-content__tip-title"
							size="medium"
							weight="extraBold"
							color="black"
						>
							{tipCard.title}
						</H3>
						<Text
							className="step-content__tip-description"
							size="standard"
							weight="standard"
							color="black"
						>
							{tipCard.description}
						</Text>
					</div>
				</div>
			)}
		</div>
	);
};

StepContent.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	steps: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			content: PropTypes.string.isRequired,
		}),
	).isRequired,
	tipCard: PropTypes.shape({
		icon: PropTypes.string,
		title: PropTypes.string.isRequired,
		description: PropTypes.string.isRequired,
	}),
};

export default StepContent;
